import {
  BoundMultiSelectField,
  BoundSwitchField,
  Button,
  cardStyle,
  CollapseToggle,
  column,
  Css,
  emptyCell,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  GridTableApi,
  multiFilter,
  NumberField,
  SelectField,
  SelectToggle,
  singleFilter,
  Switch,
  Tag,
  Tooltip,
  useComputed,
  useFilter,
  useGridTableApi,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useEffect, useMemo, useState } from "react";
import { Icon, SearchBox } from "src/components";
import { useStepperContext } from "src/components/stepper";
import {
  CheckoutConfigOptionGroupStep,
  type CheckoutConfigOptionGroupStepDetail,
  Maybe,
  SaveProductOfferingInput,
  type SaveProductOfferingOptionGroupInput,
  SaveProductOfferingOptionInput,
} from "src/generated/graphqlTypes";
import { useReaction } from "src/hooks";
import { useMatchAny } from "src/hooks/useMatchAny";
import { useRouteParams } from "src/hooks/useRouteParams";
import { BidOutCell } from "src/routes/developments/product-offerings/components/BidOutCell";
import { PrerequisitesTag } from "src/routes/developments/product-offerings/components/PrerequisitesTag";
import { ConflictTag } from "src/routes/developments/product-offerings/components/ReadOnlyOptionsTable";
import { RequiredOptionsTag } from "src/routes/developments/product-offerings/components/RequiredOptionsTag";
import { RequiredWhenTag } from "src/routes/developments/product-offerings/components/RequiredWhenTag";
import { TargetCombinationsTag } from "src/routes/developments/product-offerings/components/TargetCombinationsTag";
import { TableActions } from "src/routes/layout/TableActions";
import {
  CreateOfferingReadyPlanOptionFragment,
  useOptionsStepQuery,
} from "src/routes/libraries/product-offerings/add-product-offering/components/4.OptionsStep.generated";
import { ProductOfferingParams } from "src/routes/routesDef";
import { productOfferingEditRoute } from "src/routing/domains/librariesPaths";
import { queryResult } from "src/utils";
import { maybeAddIdColumn } from "src/utils/idColumn";
import { StringParam, useQueryParams } from "use-query-params";
import { EditProductOfferingStep } from "../utils";
import { ProductOfferingStepActions } from "./ProductOfferingStepActions";

type OptionsStepProps = { formState: ObjectState<SaveProductOfferingInput> };

export function OptionsStep({ formState }: OptionsStepProps) {
  const result = useOptionsStepQuery({
    variables: {
      input: {
        planPackageId: formState.planPackageId.value!,
        designPackageIds: [formState.exteriorDesignPackageId.value!, formState.interiorDesignPackageId.value!],
      },
    },
  });
  return queryResult(result, ({ potentialOptions }) => (
    <OptionsView readyPlanOptions={potentialOptions} formState={formState} readOnly={false} />
  ));
}

type OptionsViewProps = {
  /**
   * Create view:
   * - All options available from stepper selected plan package and design package(s) (POF.potentialOptions)
   *
   * Edit view:
   * - All options that are available to be added to the pof (POF.options)
   *  **/
  readyPlanOptions: CreateOfferingReadyPlanOptionFragment[];
  formState: ObjectState<SaveProductOfferingInput>;
  /**
   * Only used in edit view: RPO ids pinned by the checkout starting config, which cannot be
   * removed from the POF or toggled out of checkout while the starting config depends on them.
   */
  startingConfigPinnedOptionIds?: string[];
  /** RPO ids in the offering's full (calculated) checkout starting config, shown as a tag. */
  startingConfigOptionIds?: string[];
  /** When true, renders the view in read-only mode */
  readOnly: boolean;
  /** Reasons why the view is read-only, displayed in a tooltip */
  disabledReasons?: string[];
  checkoutConfigOptionGroupSteps?: CheckoutConfigOptionGroupStepDetail[];
};

export function OptionsView({
  readyPlanOptions,
  formState,
  startingConfigPinnedOptionIds,
  startingConfigOptionIds,
  readOnly,
  disabledReasons = [],
  checkoutConfigOptionGroupSteps,
}: OptionsViewProps) {
  const tableApi = useGridTableApi<Row>();
  const { productOfferingId } = useRouteParams<ProductOfferingParams>();
  const optionsTypes = useMemo(() => readyPlanOptions.map((rpo) => rpo.type).unique() ?? [], [readyPlanOptions]);

  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const [selectedPersonalizationStep, setSelectedPersonalizationStep] = useState(false);

  const filterDefs: FilterDefs<OptionTypeFilter> = useMemo(() => {
    const option = multiFilter({
      label: "Type",
      options: optionsTypes,
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    });
    const location = multiFilter({
      label: "Location",
      options: [
        { id: null, name: "None" },
        ...readyPlanOptions
          .map((rpo) => rpo.optionGroup.readyPlanLocation)
          .compact()
          .uniqueBy((rpl) => rpl.id)
          .sortBy(({ name }) => name),
      ],
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    });
    const showOptions = singleFilter({
      label: "Show Options",
      options: [
        { id: "disabled", name: "Disabled" },
        { id: "archived", name: "Archived" },
        { id: "not-in-checkout", name: "Not in Checkout" },
        { id: "not-in-underwriting", name: "Not in Underwriting" },
      ],
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id as ShowOptionsFilterValue,
    });
    const step = multiFilter({
      label: "Step",
      options: checkoutConfigOptionGroupSteps ?? [],
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ code }) => code,
    });
    const substepNumbers = readyPlanOptions
      .map((rpo) => rpo.optionGroup.substepNumber)
      .compact()
      .unique()
      .sortBy((n) => n);
    const substep = multiFilter({
      label: "Substep",
      options: substepNumbers.map((n) => ({ id: n, name: `Substep ${n}` })),
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    });
    return {
      option,
      location,
      showOptions,
      ...(checkoutConfigOptionGroupSteps?.nonEmpty ? { step } : {}),
      ...(selectedPersonalizationStep && substepNumbers.nonEmpty ? { substep } : {}),
    };
  }, [readyPlanOptions, optionsTypes, checkoutConfigOptionGroupSteps, selectedPersonalizationStep]);

  const { filter, setFilter } = useFilter({ filterDefs });
  const [collapsedGroups, setCollapsedGroups] = useState(false);

  // Beam keeps a dropped filter's value, so clear substep or it keeps filtering rows unseen.
  function onFilterChange(next: OptionTypeFilter) {
    const isPersonalization = !!next.step?.includes(CheckoutConfigOptionGroupStep.InteriorPersonalization);
    setSelectedPersonalizationStep(isPersonalization);
    setFilter(isPersonalization ? next : { ...next, substep: undefined });
  }

  const tableFormState = useFormState({
    config: tableFormConfig,
    init: {
      onlyOnce: true,
      input: {
        readyPlanOptions: readyPlanOptions.map((o) => ({
          id: o.id,
          optionGroup: { ...o.optionGroup, overrideStep: o.optionGroup.overrideStep?.code },
          optionDefaultsIfIds: o.optionDefaultsIf.map(({ id }) => id),
          order: o.order,
          publishToUnderwriting: o.publishToUnderwriting,
          showInCheckout: o.showInCheckout,
          missingInsideMapsRendering: o.missingInsideMapsRendering,
          checkoutOrder: o.checkoutOrder,
        })),
      },
    },
  });

  // On initial load either select all options if none have been selected or select the ones that have been selected
  useEffect(() => {
    let activeReadyPlanOptions = readyPlanOptions.filter((rpo) => rpo.active);
    if (formState.options.value.length > 0) {
      const selectedReadyPlanOptions = formState.options.value.map((e) => e.id);
      activeReadyPlanOptions = activeReadyPlanOptions.filter((rpo) => selectedReadyPlanOptions.includes(rpo.id));
    }
    activeReadyPlanOptions
      .filter((rpo) => rpo.optionGroup.isMultiOptionGroup)
      .forEach((rpo) => tableApi.selectRow(rpo.id, true));
    activeReadyPlanOptions
      .filter((rpo) => rpo.optionGroup.isSingleOptionGroup)
      .forEach((rpo) => tableApi.selectRow(rpo.optionGroup.id, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // It would be nice to use a single form state but because we want to persist the defaults of unselected options we need to use a separate form state
  useReaction(
    () => {
      const selectedChildRpos = tableApi.getSelectedRows("child");
      const selectedParentRpogs = tableApi.getSelectedRows("parent");
      const selectedRpoIds = selectedChildRpos
        .map((row) => row.id)
        .concat(selectedParentRpogs.map((row) => row.data.rpo.id));
      return tableFormState.readyPlanOptions.value.filter((rpo) => selectedRpoIds.includes(rpo.id));
    },
    (options) => {
      // Sanity check we don't update the formState when in read-only mode
      if (readOnly) return;
      formState.options.set(
        options.map(
          ({ id, optionDefaultsIfIds, publishToUnderwriting, showInCheckout, missingInsideMapsRendering }) => ({
            id,
            optionDefaultsIfIds,
            publishToUnderwriting,
            showInCheckout,
            missingInsideMapsRendering,
          }),
        ),
      );
    },
    [tableApi, tableFormState, readOnly],
    { fireImmediately: true },
  );

  return (
    <>
      <div css={Css.df.fdc.aic.$}>
        <div css={Css.xl2.$}>{readOnly ? "View your options" : "Select your options"}</div>
        <div css={Css.md.pt2.pbPx(60).df.aic.gap1.$}>
          {readOnly ? (
            <>
              Viewing the option configuration for this offering.
              {disabledReasons.nonEmpty && (
                <Tooltip
                  title={
                    <div>
                      {disabledReasons.map((reason, i) => (
                        <div key={i}>{reason}</div>
                      ))}
                    </div>
                  }
                >
                  <Icon icon="infoCircle" />
                </Tooltip>
              )}
            </>
          ) : (
            "Search and select the various option types to add to the plan. All conflicts and dependencies are already attached."
          )}
        </div>
      </div>
      <TableActions>
        <div css={Css.df.gap1.jcfe.$}>
          <SearchBox onSearch={setSearchFilter} clearable />
          {/* Keep all 5 inline so the row doesn't reflow when the substep filter appears/disappears. */}
          <Filters<OptionTypeFilter>
            filter={filter}
            filterDefs={filterDefs}
            onChange={onFilterChange}
            numberOfInlineFilters={5}
          />
        </div>
        <div css={Css.df.aic.gap2.$}>
          <Button
            onClick={() => setCollapsedGroups(!collapsedGroups)}
            icon={collapsedGroups ? "expand" : "collapse"}
            label={`${collapsedGroups ? "Expand" : "Collapse"} Groups`}
            variant="secondary"
          />
        </div>
      </TableActions>
      <div css={Css.w100.h100.bgWhite.p4.$}>
        <OptionsTable
          collapsedGroups={collapsedGroups}
          tableFormState={tableFormState}
          formState={formState}
          readyPlanOptions={readyPlanOptions}
          filter={filter}
          searchFilter={searchFilter}
          tableApi={tableApi}
          startingConfigPinnedOptionIds={startingConfigPinnedOptionIds}
          startingConfigOptionIds={startingConfigOptionIds}
          readOnly={readOnly}
          checkoutConfigOptionGroupSteps={checkoutConfigOptionGroupSteps}
        />
      </div>
      {!productOfferingId && <ProductOfferingStepActions />}
    </>
  );
}

type OptionsTableProps = {
  tableFormState: ObjectState<FormValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readyPlanOptions: CreateOfferingReadyPlanOptionFragment[];
  searchFilter?: string;
  filter: OptionTypeFilter;
  collapsedGroups: boolean;
  tableApi: GridTableApi<Row>;
  startingConfigPinnedOptionIds?: string[];
  startingConfigOptionIds?: string[];
  readOnly: boolean;
  checkoutConfigOptionGroupSteps?: CheckoutConfigOptionGroupStepDetail[];
};

function OptionsTable(props: OptionsTableProps) {
  const {
    tableFormState,
    formState,
    readyPlanOptions = [],
    filter,
    searchFilter = "",
    collapsedGroups,
    tableApi,
    startingConfigPinnedOptionIds,
    startingConfigOptionIds,
    checkoutConfigOptionGroupSteps,
  } = props;
  const isEditPath = !!useMatchAny([productOfferingEditRoute.path]);
  const [{ projectId, devId }] = useQueryParams({ projectId: StringParam, devId: StringParam });
  const rpos = useComputed(() => tableFormState.readyPlanOptions.rows, [tableFormState]);
  const { currentStep } = useStepperContext();
  const readOnly = props.readOnly || currentStep.value === EditProductOfferingStep.REVIEW;
  const rows: GridDataRow<Row>[] = useMemo(
    () => createRows(readyPlanOptions, rpos, filter, startingConfigPinnedOptionIds),
    [rpos, filter, readyPlanOptions, startingConfigPinnedOptionIds],
  );
  const columns = useMemo(() => {
    return createColumns(
      readyPlanOptions,
      formState,
      readOnly,
      isEditPath,
      new Set(startingConfigOptionIds),
      checkoutConfigOptionGroupSteps,
      devId || undefined,
      projectId || undefined,
    );
  }, [
    readyPlanOptions,
    formState,
    readOnly,
    isEditPath,
    startingConfigOptionIds,
    checkoutConfigOptionGroupSteps,
    devId,
    projectId,
  ]);

  useEffect(() => {
    const [collapsed, expanded] = rows.partition((r) => tableApi.isCollapsedRow(r.id));
    const target = collapsedGroups ? expanded : collapsed;
    target.forEach((r) => tableApi.toggleCollapsedRow(r.id));
  }, [tableApi, collapsedGroups, rows]);

  return (
    <GridTable
      api={tableApi}
      rows={rows}
      columns={columns}
      filter={searchFilter}
      style={tableStyles}
      rowStyles={rowStyles}
      as="virtual"
    />
  );
}

enum Columns {
  Select,
  Name,
  Details,
  DefaultOf,
  Publication,
  Checkout,
  CheckoutStepOverride,
  BidPercent,
}

function createColumns(
  rpos: CreateOfferingReadyPlanOptionFragment[],
  formState: ObjectState<SaveProductOfferingInput>,
  readOnly: boolean,
  isEditPath: boolean,
  startingConfigOptionIds: Set<string>,
  checkoutConfigOptionGroupSteps?: CheckoutConfigOptionGroupStepDetail[],
  devId?: string,
  projectId?: string,
): GridColumn<Row>[] {
  return [
    ...maybeAddIdColumn(
      column<Row>({ header: "Id", parent: ({ rpo }) => rpo.optionGroup.id, child: ({ rpo }) => rpo.id, w: "92px" }),
    ),
    column<Row>({
      id: `${Columns.Name}`,
      header: "Name",
      parent: ({ rpo, usedInStartingConfig }, row) => ({
        content: () => (
          <div css={Css.df.aic.gap2.$}>
            <CollapseToggle row={row.row} />
            <SelectToggle
              id={row.row.id}
              // Pass the reason through, not `!!`, so the disabled checkbox explains itself
              disabled={readOnly || !!rpo.latestVersion?.isUpstreamInactive || usedInStartingConfig}
            />
            {/* Ideally we would have separate Row types for multi-option/single-option groups... */}
            {rpo.optionGroup.isMultiOptionGroup && (
              <div css={Css.df.fdc.gap1.$}>
                <div /* use archive as a 'group' icon */ css={Css.df.aic.$}>
                  <Icon icon="archive" xss={Css.mrPx(5).$} />
                  <span css={Css.lg.$}>{rpo.optionGroup.name}</span>
                </div>
                {rpo.optionGroup.readyPlanLocation && (
                  <div css={Css.df.gap1.aic.$}>
                    <Tag text={rpo.optionGroup.readyPlanLocation.location.displayLocationPath} type="neutral" />
                  </div>
                )}
              </div>
            )}
            {rpo.optionGroup.isSingleOptionGroup && (
              <div css={Css.df.fdc.$}>
                <span css={Css.lg.$}>{rpo.optionGroup.name}</span>
                <div css={Css.df.gap1.aic.$}>
                  <div css={Css.xs.$}>{rpo.globalOption.code}</div>
                  {rpo.latestVersion?.isUpstreamInactive && <Tag text="Archived" type="warning" />}
                  {rpo.optionGroup.readyPlanLocation && (
                    <Tag text={rpo.optionGroup.readyPlanLocation.location.displayLocationPath} type="neutral" />
                  )}
                </div>
              </div>
            )}
          </div>
        ),
        value: `${rpo.optionGroup.name} ${rpo.globalOption.code}`,
        css: Css.sm.if(showHighlights({ rpo, isGroupRow: true, isEditPath })).bgYellow50.$,
      }),
      child: ({ rpo, usedInStartingConfig }, { row }) => ({
        content: () => (
          <div css={Css.df.aic.gap2.$}>
            <SelectToggle
              id={row.id}
              // Checkout !rpo.active to allow not-yet-inactive rpos to be turned off
              // Pass the reason through, not `!!`, so the disabled checkbox explains itself
              disabled={readOnly || (!!rpo.latestVersion?.isUpstreamInactive && !rpo.active) || usedInStartingConfig}
            />
            <div css={Css.df.fdc.$}>
              <span css={Css.mdSb.$}>{rpo.name}</span>
              <div css={Css.df.gap1.aic.$}>
                <span css={Css.xs.$}>{rpo.globalOption.code}</span>
                {rpo.latestVersion?.isUpstreamInactive && <Tag text="Archived" type="warning" />}
              </div>
            </div>
          </div>
        ),
        value: `${rpo.name} ${rpo.globalOption.code}`,
        css: Css.sm.if(showHighlights({ rpo, isEditPath })).bgYellow50.$,
      }),
      w: "300px",
    }),
    column<Row>({
      id: `${Columns.Details}`,
      header: "Details",
      parent: ({ rpo }) => ({
        content: () => <OptionDetailsTags rpo={rpo} isGroupRow startingConfigOptionIds={startingConfigOptionIds} />,
        value: optionDetailsTags(rpo, true, startingConfigOptionIds)
          .map((tag) => tag.textValue)
          .join(" "),
        css: Css.sm.if(showHighlights({ rpo, isGroupRow: true, isEditPath })).bgYellow50.$,
      }),
      child: ({ rpo }) => ({
        content: () => <OptionDetailsTags rpo={rpo} startingConfigOptionIds={startingConfigOptionIds} />,
        value: optionDetailsTags(rpo, false, startingConfigOptionIds)
          .map((tag) => tag.textValue)
          .join(" "),
        css: Css.sm.if(showHighlights({ rpo, isEditPath })).bgYellow50.$,
      }),
      w: "220px",
      mw: "220px",
    }),
    column<Row>({
      id: `${Columns.BidPercent}`,
      header: isEditPath ? "Percent Bid" : "",
      parent: ({ rpo }) => ({
        content:
          isEditPath && rpo.optionGroup.isSingleOptionGroup && (devId || projectId) ? (
            <BidOutCell bidOutPercentage={rpo.bidOutPercentage} developmentId={devId} projectId={projectId} />
          ) : (
            <></>
          ),
      }),
      child: ({ rpo }) => ({
        content: () =>
          isEditPath && (devId || projectId) ? (
            <BidOutCell bidOutPercentage={rpo.bidOutPercentage} developmentId={devId} projectId={projectId} />
          ) : (
            <></>
          ),
      }),
      w: "200px",
    }),
    column<Row>({
      id: `${Columns.Publication}`,
      header: isEditPath ? "Publish to Underwriting" : "",
      parent: ({ rpo, state }) => ({
        content:
          isEditPath && rpo.optionGroup.isSingleOptionGroup ? (
            <PublishRPOToUWCheckbox rpoState={state} formState={formState} readOnly={readOnly} />
          ) : (
            <></>
          ),
      }),
      child: ({ state }) => ({
        content: () =>
          isEditPath ? <PublishRPOToUWCheckbox rpoState={state} formState={formState} readOnly={readOnly} /> : <></>,
      }),
      w: "200px",
    }),
    column<Row>({
      id: `${Columns.Checkout}`,
      header: "Checkout",
      w: "220px",
      mw: "220px",
      parent: ({ state, usedInStartingConfig, childStates }) => ({
        content: (
          <div css={Css.df.fdc.gap2.$}>
            <ShowRPOGInCheckoutCheckbox
              rpoState={state}
              usedInStartingConfig={usedInStartingConfig}
              formState={formState}
              readOnly={readOnly}
              rpos={rpos}
              childStates={childStates}
            />
            <CheckoutRPOGOrderField rpoState={state} formState={formState} readOnly={readOnly} />
            <ShowRPOGMissingRenderingCheckbox childStates={childStates} readOnly={readOnly} />
          </div>
        ),
      }),
      child: ({ state, rpo, usedInStartingConfig }) => ({
        content: () => (
          <div css={Css.df.fdc.gap2.w100.$}>
            <ShowRPOInCheckoutCheckbox
              usedInStartingConfig={usedInStartingConfig}
              rpoState={state}
              formState={formState}
              readOnly={readOnly}
            />
            <CheckoutRPOOrderField
              rpoState={state}
              formState={formState}
              readOnly={readOnly}
              isPersonalizationOption={!!rpo.optionGroup.personalizationType}
            />
            <ShowRPOMissingRenderingCheckbox rpoState={state} readOnly={readOnly} />
          </div>
        ),
      }),
    }),
    ...(isEditPath && checkoutConfigOptionGroupSteps
      ? [
          column<Row>({
            id: `${Columns.CheckoutStepOverride}`,
            header: "Checkout Step",
            w: "240px",
            mw: "240px",
            parent: ({ state }) => ({
              content: () => (
                <div css={Css.df.fdc.gap2.w100.$}>
                  <OverrideStepField
                    rpoState={state}
                    formState={formState}
                    readOnly={readOnly}
                    checkoutConfigOptionGroupSteps={checkoutConfigOptionGroupSteps}
                  />
                  <OverrideSubstepNumberField rpoState={state} formState={formState} readOnly={readOnly} />
                </div>
              ),
            }),
            child: () => emptyCell,
          }),
        ]
      : []),
    column<Row>({
      id: `${Columns.DefaultOf}`,
      header: "Default of",
      parent: ({ rpo, state }) =>
        // show same default of select if group only has 1 member
        rpo.optionGroup.isSingleOptionGroup
          ? {
              content: () => <DefaultOfSelectField rpo={state} rpos={rpos} formState={formState} readOnly={readOnly} />,
              value: state.optionDefaultsIfIds.value?.toString(),
              css: Css.sm.if(showHighlights({ rpo, isGroupRow: true, isEditPath })).bgYellow50.$,
            }
          : { content: rpo.optionGroup.required ? "Required" : undefined, alignment: "right" },
      child: ({ state, rpo }) => ({
        content: () => <DefaultOfSelectField rpo={state} rpos={rpos} formState={formState} readOnly={readOnly} />,
        value: state.optionDefaultsIfIds.value?.toString(),
        css: Css.sm.if(showHighlights({ rpo, isEditPath })).bgYellow50.$,
      }),
      w: "200px",
    }),
  ];
}

function createRows(
  rpos: CreateOfferingReadyPlanOptionFragment[],
  rpoObjectStates: readonly ObjectState<FormOptionValue>[],
  filter: OptionTypeFilter,
  startingConfigPinnedOptionIds?: string[],
): GridDataRow<Row>[] {
  // Group by option groups
  const optionGroups = rpos
    .filter((rop) => filter.location?.includes(rop.optionGroup.readyPlanLocation?.id ?? null) ?? true)
    .filter((rpo) => filter.step?.includes(rpo.optionGroup.step?.code ?? null) ?? true)
    .filter((rpo) => filter.substep?.includes(rpo.optionGroup.substepNumber ?? null) ?? true)
    .filter((rpo) => {
      // When a showOptions filter is selected, strictly show only matching RPOs
      if (filter.showOptions === "archived") return !!rpo.latestVersion?.isUpstreamInactive;
      if (filter.showOptions === "disabled") return !rpo.active;
      if (filter.showOptions === "not-in-checkout") return !rpo.showInCheckout;
      if (filter.showOptions === "not-in-underwriting") return !rpo.publishToUnderwriting;
      // Default: hide archived/disabled, apply type filter
      return (!rpo.latestVersion?.isUpstreamInactive || rpo.active) && (filter.option?.includes(rpo.type.id) ?? true);
    })
    .groupBy((o) => o.optionGroup.id);

  // Create a map of rpo.id => its object state to avoid n^2 lookups
  const rpoStatesById = rpoObjectStates.keyBy((o) => o.id.value);
  // Create a set to avoid n^2 lookups
  const pinnedRpoIds = new Set(startingConfigPinnedOptionIds ?? []);

  // sort by group order
  return Object.entries(optionGroups)
    .sortBy(([, rpos]) => rpos[0].optionGroup.order)
    .map(([optionGroupId, rpos]) => {
      return {
        kind: "parent" as const,
        data: {
          // Because a parent row can be either a "multi-option group actual parent row" or single-option group fake
          // parent row", we always use the 1st rpo as "the parent".
          rpo: rpos[0],
          state: rpoStatesById[rpos[0].id],
          childStates: rpos.map((rpo) => rpoStatesById[rpo.id]),
          // NOTE: This logic could go away were we to prompt users to update configs that will become invalidated
          // by these updates. Not just `showInCheckout` but also if the option is available on the POF in the first place
          usedInStartingConfig: rpos.some((rpo) => pinnedRpoIds.has(rpo.id))
            ? "Disabled because the checkout starting config requires this option"
            : undefined,
        },
        id: optionGroupId,
        children: rpos[0].optionGroup.isMultiOptionGroup
          ? rpos
              .flatMap((rpo) => ({
                kind: "child" as const,
                id: rpo.id,
                data: {
                  rpo: rpo,
                  state: rpoStatesById[rpo.id],
                  // NOTE: This logic could go away were we to prompt users to update configs that will become invalidated
                  // by these updates. Not just `showInCheckout` but also if the option is available on the POF in the first place
                  usedInStartingConfig: pinnedRpoIds.has(rpo.id)
                    ? "Disabled because the checkout starting config requires this option"
                    : undefined,
                },
              }))
              .sortBy((rpo) => rpo.data.state.order.value ?? Number.POSITIVE_INFINITY)
          : undefined,
      };
    });
}

type RpoSelectFieldProps = {
  rpo: ObjectState<FormOptionValue>;
  rpos: CreateOfferingReadyPlanOptionFragment[];
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
};

function DefaultOfSelectField({ rpo, rpos, formState, readOnly }: RpoSelectFieldProps) {
  // If the formState defaultIds have been set, display those
  const formRpoDefaultIds = formState.options.rows.find((os) => os.value.id === rpo.value.id)?.optionDefaultsIfIds;
  return (
    <BoundMultiSelectField
      field={formRpoDefaultIds?.value?.nonEmpty ? formRpoDefaultIds : rpo.optionDefaultsIfIds}
      options={mapToRpoSelectFieldOptions(rpo, rpos)}
      placeholder="Default of"
      onSelect={(val) => {
        rpo.optionDefaultsIfIds.set(val);
      }}
      readOnly={readOnly}
    />
  );
}

function mapToRpoSelectFieldOptions(rpo: ObjectState<FormOptionValue>, rpos: CreateOfferingReadyPlanOptionFragment[]) {
  return rpos
    .filter((opt) => opt.optionGroup.id !== rpo.optionGroup.value?.id)
    .sortBy((opt) => opt.name)
    .map((opt) => ({ id: opt.id, name: opt.displayName }));
}

type PublishRPOToUWCheckboxProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
};

function PublishRPOToUWCheckbox(props: PublishRPOToUWCheckboxProps) {
  const { rpoState, formState, readOnly } = props;
  const selected = useComputed(
    () => Boolean(rpoState.publishToUnderwriting.value),
    [rpoState.publishToUnderwriting.value],
  );
  return (
    <Switch
      selected={selected}
      label="Publish to UW"
      labelStyle="inline"
      disabled={readOnly}
      onChange={(val) => {
        rpoState.publishToUnderwriting.set(val);
        const [updated, same] = formState.options.value.partition((o) => o.id === rpoState.id.value);
        formState.options.set([...same, { ...updated[0], publishToUnderwriting: val }]);
      }}
    />
  );
}

type ShowRPOGInCheckoutCheckboxProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
  usedInStartingConfig?: string;
  rpos: CreateOfferingReadyPlanOptionFragment[];
  childStates: ObjectState<FormOptionValue>[];
};

/** This RPOG-level toggle really just acts as bulk select/unselect all. */
function ShowRPOGInCheckoutCheckbox(props: ShowRPOGInCheckoutCheckboxProps) {
  const { rpoState, formState, readOnly, usedInStartingConfig, rpos, childStates } = props;
  // Filter all `rpos` to only ours
  const groupRpos = rpos.filter((rpo) => rpo.optionGroup.id === rpoState.optionGroup.value?.id);
  const allSelected = useComputed(() => {
    const filteredOptions = formState.options.value
      .filter((opt) => groupRpos.map((rpo) => rpo.id).includes(opt.id))
      .compact();
    return filteredOptions.nonEmpty && filteredOptions.every((opt) => !!opt.showInCheckout);
  }, [groupRpos, formState.options.value]);
  return (
    <Switch
      selected={allSelected}
      label="Checkout"
      labelStyle="inline"
      disabled={readOnly || (allSelected && usedInStartingConfig)}
      onChange={(val) => {
        childStates.forEach((childState) => {
          childState.showInCheckout.set(val);
        });
        const targetIds = groupRpos.map((rpo) => rpo.id);
        const updatedOptions = formState.options.value.map((option) =>
          targetIds.includes(option.id) ? { ...option, showInCheckout: val } : option,
        );
        formState.options.set(updatedOptions);
      }}
    />
  );
}

type ShowRPOInCheckoutCheckboxProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
  usedInStartingConfig?: string;
};

function ShowRPOInCheckoutCheckbox(props: ShowRPOInCheckoutCheckboxProps) {
  const { rpoState, formState, readOnly, usedInStartingConfig } = props;
  const selected = useComputed(
    () => Boolean(formState.options.value.find((o) => o.id === rpoState.id.value)?.showInCheckout),
    [rpoState.showInCheckout.value, formState.options.value],
  );
  return (
    <Switch
      selected={selected}
      label="Checkout"
      labelStyle="inline"
      disabled={readOnly || (selected && usedInStartingConfig)}
      onChange={(val) => {
        rpoState.showInCheckout.set(val);
        const targetIds = [rpoState.id.value];
        const updatedOptions = formState.options.value.map((option) =>
          targetIds.includes(option.id) ? { ...option, showInCheckout: val } : option,
        );
        formState.options.set(updatedOptions);
      }}
    />
  );
}

type ShowRPOGMissingRenderingCheckboxProps = { childStates: ObjectState<FormOptionValue>[]; readOnly: boolean };

/** Bulk-select missing Inside Maps rendering for checkout-visible children in this group. */
function ShowRPOGMissingRenderingCheckbox(props: ShowRPOGMissingRenderingCheckboxProps) {
  const { childStates, readOnly } = props;
  const { allSelected, hasCheckoutVisibleChildren } = useComputed(() => {
    const checkoutVisibleChildStates = childStates.filter((childState) => Boolean(childState.showInCheckout.value));
    return {
      hasCheckoutVisibleChildren: checkoutVisibleChildStates.nonEmpty,
      allSelected:
        checkoutVisibleChildStates.nonEmpty &&
        checkoutVisibleChildStates.every((childState) => Boolean(childState.missingInsideMapsRendering.value)),
    };
  }, [childStates]);
  if (!hasCheckoutVisibleChildren) return <></>;
  return (
    <Switch
      selected={allSelected}
      label="Missing rendering"
      labelStyle="inline"
      tooltip="Shows a warning in Checkout when this option is selected that we can't render this option in the visualization. This field is unversioned so updates will appear immediately."
      disabled={readOnly}
      onChange={(val) => {
        childStates
          .filter((childState) => Boolean(childState.showInCheckout.value))
          .forEach((childState) => childState.missingInsideMapsRendering.set(val));
      }}
    />
  );
}

type ShowRPOMissingRenderingCheckboxProps = { rpoState: ObjectState<FormOptionValue>; readOnly: boolean };

function ShowRPOMissingRenderingCheckbox(props: ShowRPOMissingRenderingCheckboxProps) {
  const { rpoState, readOnly } = props;
  const showInCheckout = useComputed(() => Boolean(rpoState.showInCheckout.value), [rpoState]);
  if (!showInCheckout) return <></>;
  return (
    <BoundSwitchField
      field={rpoState.missingInsideMapsRendering}
      label="Missing rendering"
      labelStyle="inline"
      tooltip="Shows a warning in Checkout when this option is selected that we can't render this option in the visualization. This field is unversioned so updates will appear immediately."
      disabled={readOnly}
    />
  );
}

type CheckoutRPOGOrderFieldProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
};

function CheckoutRPOGOrderField(props: CheckoutRPOGOrderFieldProps) {
  const { rpoState, formState, readOnly } = props;
  const { value, showInCheckout } = useComputed(
    () => ({
      value: rpoState.optionGroup.checkoutOrder.value ?? undefined,
      showInCheckout: rpoState.showInCheckout.value,
    }),
    [rpoState],
  );
  return (
    <NumberField
      data-testid="checkoutOrder"
      label="Order"
      labelStyle="inline"
      fullWidth
      value={value}
      onChange={(val) => {
        // Update the local state for this RPO
        rpoState.optionGroup.checkoutOrder.set(val);
        // Update the optionGroups in formState to track RPOG-level changes
        const groupState = formState.optionGroups.rows.find((g) => g.value.id === rpoState.optionGroup.id.value);
        groupState?.checkoutOrder.set(val);
      }}
      readOnly={readOnly || !showInCheckout}
    />
  );
}

type OverrideStepFieldProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
  checkoutConfigOptionGroupSteps: CheckoutConfigOptionGroupStepDetail[];
};

function OverrideStepField(props: OverrideStepFieldProps) {
  const { rpoState, formState, readOnly, checkoutConfigOptionGroupSteps } = props;
  const options = [{ code: undefined, name: "Default" }, ...checkoutConfigOptionGroupSteps];
  const value = useComputed(() => rpoState.optionGroup.overrideStep.value, [rpoState]);
  return (
    <SelectField
      data-testid="overrideStep"
      label="Step"
      labelStyle="inline"
      placeholder="Default"
      options={options}
      autoSort={false}
      value={value}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.code}
      onSelect={(val) => {
        const nextValue = val ?? null;
        rpoState.optionGroup.overrideStep.set(nextValue);
        const groupState = formState.optionGroups.rows.find((g) => g.value.id === rpoState.optionGroup.id.value);
        groupState?.overrideStep.set(nextValue);
        // Only the interior steps can have a substep
        if (nextValue !== CheckoutConfigOptionGroupStep.InteriorPersonalization) {
          rpoState.optionGroup.overrideSubstepNumber.set(null);
          groupState?.overrideSubstepNumber.set(null);
        }
      }}
      // We used to "check showInCheckout" here but:
      // a) the step override is fundamentally an RPOG setting, and the RPOG 'showInCheckout' is
      // really a bulk select, not a true yes/no stored value.
      // b) RPOGs can have partial `showInCheckout` RPOs still might need their step nudged around;
      // maybe if _literally every_ RPO was `showInCheckout=false`, we could say this is disabled,
      // but that's not worth it atm.
      readOnly={readOnly}
    />
  );
}

type OverrideSubstepNumberFieldProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
};

function OverrideSubstepNumberField(props: OverrideSubstepNumberFieldProps) {
  const { rpoState, formState, readOnly } = props;
  const value = useComputed(() => rpoState.optionGroup.overrideSubstepNumber.value ?? undefined, [rpoState]);
  return (
    <NumberField
      data-testid="overrideSubstepNumber"
      label="Substep #"
      labelStyle="inline"
      fullWidth
      value={value}
      onChange={(val) => {
        const nextValue = val ?? null;
        rpoState.optionGroup.overrideSubstepNumber.set(nextValue);
        const groupState = formState.optionGroups.rows.find((g) => g.value.id === rpoState.optionGroup.id.value);
        groupState?.overrideSubstepNumber.set(nextValue);
      }}
      // See comment on Step field for why we don't check showInCheckout here
      readOnly={readOnly}
      clearable
    />
  );
}

type CheckoutRPOOrderFieldProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
  // When true, this RPO belongs to a personalization RPOG, whose checkout order is set in the design package and cannot be edited here.
  isPersonalizationOption: boolean;
};

function CheckoutRPOOrderField(props: CheckoutRPOOrderFieldProps) {
  const { rpoState, formState, readOnly, isPersonalizationOption } = props;
  const { value, showInCheckout } = useComputed(
    () => ({ value: rpoState.checkoutOrder.value ?? undefined, showInCheckout: rpoState.showInCheckout.value }),
    [rpoState],
  );
  return (
    <NumberField
      data-testid="optionCheckoutOrder"
      label="Order"
      labelStyle="inline"
      fullWidth
      value={value}
      onChange={(val) => {
        // Update the local state for this RPO
        rpoState.checkoutOrder.set(val);
        // Update the options in formState to track RPO-level changes
        const optionState = formState.options.rows.find((o) => o.value.id === rpoState.id.value);
        optionState?.checkoutOrder.set(val);
      }}
      readOnly={readOnly || !showInCheckout}
      disabled={
        isPersonalizationOption && "Checkout order of personalization options must be set in the design package."
      }
    />
  );
}

type HeaderRow = { kind: "header"; id: string; data: undefined };

type ParentRow = {
  kind: "parent";
  id: string;
  data: {
    // This is janky, but the parent uses "the first rpo", instead of the rpog directly
    rpo: CreateOfferingReadyPlanOptionFragment;
    state: ObjectState<FormOptionValue>;
    childStates: ObjectState<FormOptionValue>[];
    usedInStartingConfig?: string;
  };
};

type ChildRow = {
  kind: "child";
  id: string;
  data: {
    rpo: CreateOfferingReadyPlanOptionFragment;
    state: ObjectState<FormOptionValue>;
    usedInStartingConfig?: string;
  };
};

type Row = HeaderRow | ParentRow | ChildRow;

const tableStyles = {
  ...cardStyle,
  cellCss: { ...cardStyle.cellCss, ...Css.aic.pPx(12).$ },
  presentationSettings: { borderless: false },
};

const rowStyles = { child: { rowCss: Css.bgGray100.$, cellCss: Css.bgGray100.$ } };

type ShowOptionsFilterValue = "disabled" | "archived" | "not-in-checkout" | "not-in-underwriting";
type OptionTypeFilter = {
  option?: string[];
  location?: Maybe<string>[];
  showOptions?: ShowOptionsFilterValue;
  step?: Maybe<CheckoutConfigOptionGroupStep>[];
  substep?: Maybe<number>[];
};

type FormOptionValue = SaveProductOfferingOptionInput & {
  optionGroup: SaveProductOfferingOptionGroupInput;
  order: number;
  checkoutOrder?: number | null;
};

type FormValue = { readyPlanOptions: FormOptionValue[] };

const tableFormConfig: ObjectConfig<FormValue> = {
  readyPlanOptions: {
    type: "list",
    config: {
      id: { type: "value" },
      checkoutOrder: { type: "value" },

      optionGroup: {
        type: "object",
        config: {
          id: { type: "value" },
          checkoutOrder: { type: "value" },
          overrideStep: { type: "value" },
          overrideSubstepNumber: { type: "value" },
        },
      },
      optionDefaultsIfIds: { type: "value" },
      order: { type: "value" },
      publishToUnderwriting: { type: "value" },
      showInCheckout: { type: "value" },
      missingInsideMapsRendering: { type: "value" },
    },
  },
};

type OptionDetailsTagsProps = {
  rpo: CreateOfferingReadyPlanOptionFragment;
  isGroupRow?: boolean;
  startingConfigOptionIds?: Set<string>;
};

type OptionDetailsTag = { key: string; element: JSX.Element; textValue: string };

/** Helper component that shows the "Details" column of the tags. */
function OptionDetailsTags(props: OptionDetailsTagsProps) {
  const { rpo, isGroupRow = false, startingConfigOptionIds } = props;
  const tags = optionDetailsTags(rpo, isGroupRow, startingConfigOptionIds);
  if (tags.isEmpty) return null;
  return (
    <div css={Css.df.fdc.aifs.gap1.$}>
      {tags.map((tag) => (
        <div key={tag.key}>{tag.element}</div>
      ))}
    </div>
  );
}

/** Creates a `OptionDetailsTags` list to be used either in the component or our textValue. */
function optionDetailsTags(
  rpo: CreateOfferingReadyPlanOptionFragment,
  isGroupRow: boolean,
  startingConfigOptionIds?: Set<string>,
): OptionDetailsTag[] {
  const { prerequisites, forGroup } = getOptionDetailsPrerequisites(rpo, isGroupRow);
  const conflicts = rpo.optionConflicts.concat(rpo.optionConflictChildren);
  // A multi-option group's parent row borrows its first RPO, so per-option tags would be misleading there.
  const showPerOptionTags = !isGroupRow || rpo.optionGroup.isSingleOptionGroup;
  const showConflicts = showPerOptionTags;
  const inStartingConfig = showPerOptionTags && !!startingConfigOptionIds?.has(rpo.id);
  const isRequiredScope = rpo.optionGroup.requiredWhen?.some((clause) => clause.all.isEmpty && clause.none.isEmpty);
  function formatOptionDetailsTextValue(item: { name: string; code?: string }) {
    return [item.name, item.code].compact().join(" ");
  }
  return [
    // The type is the same for every option in the group, so only show it on the group row
    isGroupRow && rpo.type.name
      ? {
          key: `type-${rpo.type.id}-group`,
          element: <Tag text={rpo.type.name} type="info" />,
          textValue: rpo.type.name,
        }
      : undefined,
    prerequisites.nonEmpty
      ? {
          key: `prerequisites-${rpo.id}-${isGroupRow ? "group" : "child"}`,
          element: <PrerequisitesTag prerequisites={prerequisites} forGroup={forGroup} />,
          textValue: prerequisites.map((prerequisite) => formatOptionDetailsTextValue(prerequisite)).join(" "),
        }
      : undefined,
    // A null `requiredWhen` means the group has no expression, so the legacy `required` flag applies instead
    isGroupRow && rpo.optionGroup.requiredWhen
      ? {
          key: `required-when-${rpo.optionGroup.id}`,
          element: isRequiredScope ? (
            <Tag text="Required Scope" type="update" />
          ) : (
            <RequiredWhenTag requiredWhen={rpo.optionGroup.requiredWhen} />
          ),
          textValue: isRequiredScope
            ? "Required Scope"
            : rpo.optionGroup.requiredWhen
                .flatMap((clause) => clause.all.concat(clause.none))
                .map((o) => formatOptionDetailsTextValue(o))
                .join(" "),
        }
      : undefined,
    inStartingConfig
      ? {
          key: `starting-config-${rpo.id}-${isGroupRow ? "group" : "child"}`,
          element: <Tag text="Starting Config" type="success" />,
          textValue: "Starting Config",
        }
      : undefined,
    showConflicts && conflicts.nonEmpty
      ? {
          key: `conflicts-${rpo.id}-${isGroupRow ? "group" : "child"}`,
          element: <ConflictTag rpo={rpo} />,
          textValue: conflicts.map((conflict) => formatOptionDetailsTextValue(conflict)).join(" "),
        }
      : undefined,
    rpo.optionRequiredIf.nonEmpty
      ? {
          key: `required-options-${rpo.id}-${isGroupRow ? "group" : "child"}`,
          element: <RequiredOptionsTag optionRequiredIf={rpo.optionRequiredIf} />,
          textValue: rpo.optionRequiredIf
            .flatMap((rpro) => rpro.targetCombinations)
            .map((combo) => combo.options.map((o) => o.name).join("/"))
            .join(", "),
        }
      : undefined,
    // Personalization options are the default for a given combination of options, so show which ones (if any)
    !isGroupRow && rpo.optionGroup.personalizationType
      ? {
          key: `target-combinations-${rpo.id}`,
          element: <TargetCombinationsTag combinations={rpo.personalizationTargetCombinations} />,
          textValue: rpo.personalizationTargetCombinations.isEmpty
            ? "No combos"
            : rpo.personalizationTargetCombinations
                .map((combo) => combo.options.map((o) => o.name).join("/"))
                .join(", "),
        }
      : undefined,
  ].compact();
}

/** Multi-option group rows show the group's prerequisites, everything else shows the option's own. */
function getOptionDetailsPrerequisites(rpo: CreateOfferingReadyPlanOptionFragment, isGroupRow: boolean) {
  // If this is singleOptionGroup, its an addon that can't have groupPrereqs, so only show the rpog.prereqs.
  // If this is !singleOptionGroup, its a group and the children will show rpo.prereqs, so we can show just groupPrereqs here.
  const forGroup = isGroupRow && !rpo.optionGroup.isSingleOptionGroup;
  return { forGroup, prerequisites: forGroup ? rpo.optionGroup.groupPrerequisites : rpo.optionPrerequisites };
}

/**
 * Highlight option rows (and singleOptionGroup rows) if the option is newly added from upstream
 * And while in the edit path (which is only allowed for the latest version)
 * (This options table is also used in the create path, and for older versions)
 */
function showHighlights({
  rpo,
  isEditPath,
  isGroupRow = false,
}: {
  rpo: CreateOfferingReadyPlanOptionFragment;
  isEditPath: boolean;
  isGroupRow?: boolean;
}) {
  const renderHighlights =
    (!!rpo.draftVersion?.isNewFromUpstream || !!rpo.draftVersion?.isUpstreamInactive) && isEditPath;
  return renderHighlights && (!isGroupRow || !!rpo.optionGroup.isSingleOptionGroup);
}
